import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { withALErrorBoundary } from "@helpers/ErrorBoundary/ALErrorBoundary";
import { useTracking } from "@context/Tracking";
import useDeviceDetect from "@hooks/useDeviceDetect";
import { ComponentType } from "@constants/ComponentType";
import { ALLink } from "../ALComponents";
import { isBrowser } from "@helpers/browser";
import { cn } from "@helpers/general";
import ALTERNATIVES_ATTRIBUTES_ORDER from "@constants/AlternativesAttributesOrder";

function ProductAlternativeColorProduct({
  isActive,
  colorCode,
  colorName,
  activeProductHandle,
  activeProductColor,
  handle,
  availableForSale,
  componentType,
  handleAlternativeClick,
  isProductPage,
}) {
  const { trackClickAlternative } = useTracking();

  const [linkToAlternative, setLinkToAlternative] = useState(`/p/${handle}`);

  useEffect(() => {
    if (isBrowser && window.location.search) {
      setLinkToAlternative(`/p/${handle}/${window.location.search}`);
    }
  }, [handle]);

  const alternativeClick = () => {
    trackClickAlternative({
      page: componentType,
      activeProductHandle,
      activeProductColor,
      alternativeHandle: handle,
      alternativeColor: colorName,
    });

    if (componentType === ComponentType.CP) {
      handleAlternativeClick(handle);
    }
  };

  let bubbleColor = colorCode || "transparent";
  if (colorCode.includes("/")) {
    bubbleColor = `linear-gradient( 90deg, ${colorCode.split("/")[0]}, ${
      colorCode.split("/")[0]
    } 50%, ${colorCode.split("/")[1]} 50% )`;
  }

  const whiteClear =
    colorName.toLowerCase() === "white" ||
    colorName.toLowerCase() === "clear" ||
    bubbleColor.includes("#FFFFFF");

  return (
    <span
      key={`alternative-${handle}-color`}
      className={`${cn(
        "relative flex justify-center items-center border rounded-[4px]",
        componentType === ComponentType.CP && "p-[2px]",
        (isProductPage && isActive && "ring ring-1 ring-black ring-offset-2") ||
          (componentType === ComponentType.CP && isActive && "border-black") ||
          (isProductPage && "border-white") ||
          (componentType === ComponentType.CP && "border-white") ||
          "border-dark-grey"
      )}`}
    >
      {!availableForSale && (
        <span className="absolute top-1/2 right-1/2 translate-x-1/2 -translate-y-1/2 rotate-[-45deg] scale-[1.5] border-b border-dark-grey w-[32px]" />
      )}

      {componentType === ComponentType.PDP && (
        <ALLink
          to={`/products/${handle}`}
          onClick={alternativeClick}
          className={cn(
            "w-[38px] h-[38px] mobile:w-[33px] mobile:h-[33px] border border-dark-grey rounded-[3px]"
          )}
          style={{ background: bubbleColor }}
          title={colorName || ""}
        >
          &nbsp;
        </ALLink>
      )}
      {componentType === ComponentType.PDP_ADS && (
        <ALLink
          to={linkToAlternative}
          onClick={alternativeClick}
          className={cn(
            "w-[40px] h-[40px] mobile:w-[35px] mobile:h-[35px] border border-dark-grey rounded-[4px]",
            whiteClear && "border-[#ccc]"
          )}
          style={{ background: bubbleColor }}
          title={colorName || ""}
        >
          &nbsp;
        </ALLink>
      )}
      {componentType === ComponentType.CP && (
        // eslint-disable-next-line jsx-a11y/control-has-associated-label
        <button
          type="button"
          className={cn(
            "w-[16px] h-[16px] mobile:w-[14px] mobile:h-[14px]",
            "rounded-[2px]",
            isActive && "ml-0",
            "border border-dark-grey"
          )}
          style={{ background: bubbleColor }}
          onClick={alternativeClick}
          title={colorName || ""}
        >
          &nbsp;
        </button>
      )}
      {componentType === ComponentType.CP_LAZY && (
        <a
          href={`/products/${handle}`}
          className={cn(
            "w-[16px] h-[16px] mobile:w-[14px] mobile:h-[14px] rounded-[4px]",
            "rounded-[2px] ml-[-3px]",
            isActive && "ml-0",
            whiteClear && "border border-[#ccc]"
          )}
          style={{ background: bubbleColor }}
          title={colorName || ""}
        >
          <span className="hidden">{colorName}</span>
          &nbsp;
        </a>
      )}
    </span>
  );
}

ProductAlternativeColorProduct.propTypes = {
  isActive: PropTypes.bool.isRequired,
  colorCode: PropTypes.string,
  colorName: PropTypes.string,
  activeProductHandle: PropTypes.string.isRequired,
  activeProductColor: PropTypes.string,
  handle: PropTypes.string.isRequired,
  availableForSale: PropTypes.bool.isRequired,
  componentType: PropTypes.string.isRequired,
  handleAlternativeClick: PropTypes.func,
  isProductPage: PropTypes.bool,
};

function ProductAlternativeColor({
  attrName = "Color",
  pageProductHandle,
  alternatives = [],
  componentType,
  handleAlternativeClick,
}) {
  const { isMobile } = useDeviceDetect();
  const [sortedAlternatives, setSortedAlternatives] = useState(alternatives);
  const [hasMoreThanThree, setHasMoreThanThree] = useState(-1);

  useEffect(() => {
    const sortedAlternativesCopy = [...alternatives];
    sortedAlternativesCopy.sort((a, b) => {
      const aIndex = ALTERNATIVES_ATTRIBUTES_ORDER.indexOf(a.config);
      const bIndex = ALTERNATIVES_ATTRIBUTES_ORDER.indexOf(b.config);
      const aColorName = a.attrArgs[1];
      const bColorName = b.attrArgs[1];

      // If neither is in the list, go to the end in alphabetical order
      if (aIndex === -1 && bIndex === -1) return aColorName < bColorName ? -1 : 1;

      // Unknown values go to the end
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;

      return aIndex - bIndex;
    });

    const MAX_NUMBER = isMobile ? 5 : 6;
    if (ComponentType.CP === componentType && sortedAlternativesCopy.length > MAX_NUMBER) {
      setHasMoreThanThree(sortedAlternativesCopy.length - 3);
      setSortedAlternatives(sortedAlternativesCopy.slice(0, 3));
    } else {
      setSortedAlternatives(sortedAlternativesCopy);
      setHasMoreThanThree(-1);
    }
  }, [alternatives, componentType, isMobile, pageProductHandle]);

  if (!sortedAlternatives?.length) return null;

  const isProductPage =
    componentType === ComponentType.PDP || componentType === ComponentType.PDP_ADS;

  return (
    <div
      className={`${cn(
        "mb-[20px]",
        "mobile:mb-[25px]",
        componentType === ComponentType.CP && "m-0 mt-[10px] mobile:mt-[8px] mobile:mb-0"
      )}`}
    >
      <div className="mb-[10px] typo-body font-bold">
        {componentType !== ComponentType.CP && (
          <>
            <span className="pr-[5px] ">{attrName}</span>
            <span className="text-dark-grey font-normal">
              {sortedAlternatives.find((a) => a.handle === pageProductHandle)?.attrArgs[1] || ""}
            </span>
          </>
        )}
      </div>
      <div
        className={`${cn(
          "flex flex-wrap gap-[10px] mobile:gap-[10px] cursor-pointer",
          isProductPage && "gap-[10px]",
          componentType === ComponentType.CP && "mobile:gap-[12px]"
        )}`}
      >
        {sortedAlternatives
          .filter((a) => a.isFound)
          .map((a) => {
            const [colorCode, colorName] = a.attrArgs;
            return (
              <ProductAlternativeColorProduct
                key={a.handle}
                isActive={pageProductHandle === a.handle}
                colorCode={colorCode}
                colorName={colorName}
                handle={a.handle}
                availableForSale={a.availableForSale}
                activeProductHandle={pageProductHandle}
                activeProductColor={
                  sortedAlternatives.find((pa) => pa.handle === pageProductHandle)?.colorName
                }
                componentType={componentType}
                handleAlternativeClick={handleAlternativeClick}
                isProductPage={isProductPage}
              />
            );
          })}
        {hasMoreThanThree > 0 && (
          <ALLink className="typo-micro flex items-center" to={`/products/${pageProductHandle}`}>
            + {hasMoreThanThree} more
          </ALLink>
        )}
      </div>
    </div>
  );
}

ProductAlternativeColor.propTypes = {
  pageProductHandle: PropTypes.string.isRequired,
  attrName: PropTypes.string,
  alternatives: PropTypes.arrayOf(
    PropTypes.shape({
      attrArgs: PropTypes.arrayOf(PropTypes.string).isRequired,
      isFound: PropTypes.bool.isRequired,
      handle: PropTypes.string.isRequired,
      attrName: PropTypes.string.isRequired,
      availableForSale: PropTypes.bool.isRequired,
    })
  ),
  componentType: PropTypes.string.isRequired,
  handleAlternativeClick: PropTypes.func,
};

export default withALErrorBoundary({
  name: "ProductAlternativeColor",
  priority: "P2",
})(ProductAlternativeColor);
